import {driver, type Driver, type Config} from 'driver.js';
import {useLocalStorage} from '../localStorage';
import {tours, type TourDefinition} from './tours';

const registeredComponents = ref(new Set<string>());

export const tourRunning = ref(false);

export function useGuidedTours() {
  const userStore = useUserStore();
  const userSettingsStore = useUserSettingsStore();
  const {getItem, setItem} = useLocalStorage();

  // User data
  const isSuperuser = computed(() => userStore.isSuperUser);
  const userGroups = computed(() => userStore.user?.groups.map((g) => g.name) || []);
  const language = computed(() => userSettingsStore.userSettings.language?.split('-')[0] || 'en');
  const hasWidgets = computed(() => (userSettingsStore.userSettings.widgets?.length ?? 0) > 0);

  const completedTours = ref<string[]>(getItem<string[]>('completedTours') || []);

  const normalizeTourKey = (key: string) => key.replace(/-(en|nl)$/i, '');

  const markTourCompleted = (tourKey: string) => {
    const normalizedKey = normalizeTourKey(tourKey);
    if (!completedTours.value.includes(normalizedKey)) {
      completedTours.value.push(normalizedKey);
      setItem('completedTours', completedTours.value);
    }
  };

  const isTourCompleted = (tourKey: string): boolean => {
    return completedTours.value.includes(normalizeTourKey(tourKey));
  };

  let driverInstance: Driver | null = null;

  const driverConfig: Partial<Config> = {
    showProgress: true,
    showButtons: ['next', 'previous', 'close'],
    stageRadius: 12,
    stagePadding: 6,
    animate: true,
    disableActiveInteraction: true,
    popoverClass: 'guided-tour-popover',
  };

  const stateActions = {
    dashboardEdit: (enable: boolean) => {
      if (enable) {
        const editBtn = document.querySelector('[name="edit-dashboard-button"]') as HTMLElement;
        if (editBtn) editBtn.click();
      } else {
        const doneBtn = document.querySelector('[name="done-button"]') as HTMLElement;
        if (doneBtn) doneBtn.click();
      }
    },
    // The widget library is opened with the toolbar's add-widgets button, and closed with its Done.
    // Resolves once the dialog is really open or closed: the tour's popover goes inside an open one.
    widgetDrawer: async (open: boolean) => {
      const getDrawer = () =>
        document.querySelector<HTMLDialogElement>('[data-testid="dashboard.toolbar-dialog"]');
      if (open && getDrawer()?.open !== true) {
        document.querySelector<HTMLElement>('[name="edit-widgets-button"]')?.click();
      } else if (!open && getDrawer()?.open === true) {
        getDrawer()?.querySelector<HTMLElement>('[name="cancel"]')?.click();
      }
      for (let i = 0; i < 30 && (getDrawer()?.open === true) !== open; i++) {
        await new Promise((resolve) => setTimeout(resolve, 20));
      }
    },
    settingsOpen: (open: boolean) => {
      const dialog = document.querySelector('#dialog-settings') as HTMLDialogElement;
      if (open && dialog && !dialog.hasAttribute('open')) {
        dialog.setAttribute(
          'style',
          'position: absolute; top: 0%; padding: 1rem; height: 75vh !important',
        );
        dialog.show();
      } else if (!open && dialog && dialog.hasAttribute('open')) {
        dialog.close();
        dialog.removeAttribute('style');
      }
    },
    settingsTab: (tabId: string) => {
      const tabElement = document.querySelector(`#${tabId}`) as HTMLElement;
      if (tabElement) {
        // Unreal levels of hacky solutions but think this is our only option without
        // rewriting the dialog to not use <dialog> element anywhere
        // overwrites the showModal function temporarily and then restores it after
        // clicking the tab to avoid conflicts with the dialog's open state watcher

        const dialog = document.querySelector('#dialog-settings') as HTMLDialogElement;
        if (dialog) {
          const originalShowModal = dialog.showModal;
          dialog.showModal = () => {
            // Prevent showModal if dialog is already open via show()
            if (!dialog.hasAttribute('open')) {
              originalShowModal.call(dialog);
            }
          };

          tabElement.click();

          // Restore original showModal after a delay
          setTimeout(() => {
            dialog.showModal = originalShowModal;
          }, 100);
        }
      }
    },
    sidebarHide: (hide: boolean) => {
      const sidebar = document.querySelector('#sidebar') as HTMLElement;
      if (!sidebar) return;

      if (hide) {
        sidebar.style.display = 'none';
      } else {
        sidebar.style.display = '';
      }
    },
  };

  // Apply desired per-step state before driver targets the element
  const applyStepState = async (state?: any) => {
    if (!state) return;

    if (typeof state.sidebarHide === 'boolean') {
      stateActions.sidebarHide(state.sidebarHide);
    }

    // The drawer needs edit mode, and has to close before leaving it
    if (state.widgetDrawer === false) await stateActions.widgetDrawer(false);

    // Dashboard edit mode
    if (typeof state.dashboardEdit === 'boolean') {
      if (state.dashboardEdit) {
        stateActions.dashboardEdit(true);
      } else {
        stateActions.dashboardEdit(false);
      }
    }

    if (state.widgetDrawer === true) {
      await nextTick(); // The add-widgets button only exists once edit mode is on
      await stateActions.widgetDrawer(true);
    }

    if (typeof state.settingsOpen === 'boolean') {
      if (state.settingsOpen) {
        stateActions.settingsOpen(true);
        if (state.settingsTab) {
          // Add delay to ensure dialog is fully rendered before clicking tab
          setTimeout(() => stateActions.settingsTab(state.settingsTab), 200);
        }
      } else {
        stateActions.settingsOpen(false);
      }
    }
  };

  // Prepare a step: apply state for the step
  const prepareStep = async (tour: TourDefinition, index: number) => {
    if (index < 0 || index >= tour.steps.length) return;

    // Apply step state if available
    await applyStepState(tour.stepStates?.[index]);
    await nextTick();
  };

  // The steps about adding widgets need something in the widget library to add.
  const addWidgetsSelectors = [
    '[id="cores.dashboard.widgettoolbar.editwidgets"]',
    '[data-testid="dashboard.toolbar-dialog"]',
  ];
  const withoutAddWidgetsSteps = (tour: TourDefinition): TourDefinition => {
    const hasWidgetsToAdd =
      document.querySelectorAll('[data-testid="cores.dashboard.gridstack.drawer"] .grid-stack-item')
        .length > 0;
    if (hasWidgetsToAdd) return tour;
    const keep = tour.steps.map((step) => !addWidgetsSelectors.includes(String(step.element)));
    return {
      ...tour,
      steps: tour.steps.filter((_, i) => keep[i]),
      stepStates: tour.stepStates?.filter((_, i) => keep[i]),
    };
  };

  const startTour = async (tourKey: string) => {
    const tour = tours[tourKey] && withoutAddWidgetsSteps(tours[tourKey]);
    if (!tour) return;

    tourRunning.value = true;
    await prepareStep(tour, 0);

    const config: Partial<Config> = {
      ...driverConfig,
      onNextClick: async (element, step, {state, driver}) => {
        const nextIndex = (state.activeIndex || 0) + 1;
        await prepareStep(tour, nextIndex);
        driver.moveNext();
      },
      onPrevClick: async (element, step, {state, driver}) => {
        const prevIndex = (state.activeIndex || 0) - 1;
        await prepareStep(tour, prevIndex);
        driver.movePrevious();
      },
      onDestroyed: () => {
        // Clean up any state changes
        const tourType = normalizeTourKey(tourKey);
        if (tourType === 'dashboard') {
          stateActions.widgetDrawer(false);
          stateActions.dashboardEdit(false);
        } else if (tourType === 'user-invitation') {
          stateActions.settingsOpen(false);
          stateActions.sidebarHide(false); // Restore sidebar
        }

        tourRunning.value = false;
        markTourCompleted(tourKey);
        driverInstance = null;
      },
    };

    driverInstance = driver({...config, steps: tour.steps});
    driverInstance.drive();
  };

  // Check if tour requirements are met
  const isTourAvailable = (tour: TourDefinition): boolean => {
    // Check role permissions
    if (tour.allowedRoles?.length && !isSuperuser.value) {
      const hasPermission = tour.allowedRoles.some((role) => userGroups.value.includes(role));
      if (!hasPermission) return false;
    }

    // Special case: dashboard tour needs widgets
    const tourType = normalizeTourKey(Object.keys(tours).find((key) => tours[key] === tour) || '');
    if (tourType === 'dashboard' && !hasWidgets.value) {
      return false;
    }

    return true;
  };

  // Component registration system
  const registerTourComponent = (tourKey: string) => {
    registeredComponents.value.add(tourKey);
  };

  const unregisterTourComponent = (tourKey: string) => {
    registeredComponents.value.delete(tourKey);
  };

  // Computed property for reactive available tours based on registered components
  const availableToursForRegisteredComponents = computed(() => {
    const langSuffix = `-${language.value}`;
    const {isMobile} = useBsBreakpoints();

    return Object.entries(tours)
      .filter(([key]) => key.endsWith(langSuffix))
      .filter(([key]) => {
        const baseTourKey = key.replace(langSuffix, '');
        return registeredComponents.value.has(baseTourKey);
      })
      .filter(([, tour]) => isTourAvailable(tour))
      .filter(([, tour]) => !isMobile.value || !tour.hiddenOnMobile)
      .map(([key, tour]) => ({
        key,
        name: tour.name,
        completed: isTourCompleted(key),
      }));
  });

  // Convenience function for components to register themselves
  const useTourRegistration = (tourKey: string) => {
    let isRegistered = false;

    const register = () => {
      if (!isRegistered) {
        registerTourComponent(tourKey);
        isRegistered = true;
      }
    };

    const unregister = () => {
      if (isRegistered) {
        unregisterTourComponent(tourKey);
        isRegistered = false;
      }
    };

    // Auto-register on mount
    onMounted(() => {
      register();
    });

    // Auto-unregister on unmount (for regular components)
    onUnmounted(() => {
      unregister();
    });

    // Auto-unregister on deactivate (for keep-alive components)
    onDeactivated(() => {
      unregister();
    });

    // Re-register on activate (for keep-alive components)
    onActivated(() => {
      register();
    });

    return {
      register,
      unregister,
    };
  };

  return {
    startTour,
    availableToursForRegisteredComponents,
    isTourCompleted,
    useTourRegistration,
  };
}
